/**
 * Story builder — she picks a character, a place, a problem and how it ends,
 * then draws and tells the pages.
 *
 * Every story carries a problem AND a resolution, because that is the language
 * target: an original story that goes somewhere. Text entry is dictation or her
 * own invented spelling, and `neverCorrectSpelling` is fixed true — a spec that
 * corrects her spelling cannot be expressed.
 */

import { z } from 'zod'
import { EmojiSchema, IdSchema, LabelSchema, SentenceSchema, isUnique, specFor } from '../common'

export const StoryChoiceSchema = z.object({
  id: IdSchema,
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
})

export const StoryBuilderConfigSchema = z
  .object({
    characters: z.array(StoryChoiceSchema).min(2).max(8),
    settings: z.array(StoryChoiceSchema).min(2).max(8),
    problems: z.array(StoryChoiceSchema).min(2).max(8),
    resolutions: z.array(StoryChoiceSchema).min(2).max(8),
    /** Three pages minimum: beginning, middle, end. */
    pageCount: z.number().int().min(3).max(8),
    textEntry: z.object({
      enabled: z.boolean(),
      mode: z.enum(['dictation', 'invented_spelling', 'none']),
      /** Her spelling stands as she wrote it. */
      neverCorrectSpelling: z.literal(true).default(true),
    }),
    drawingEnabled: z.boolean().default(true),
    /** Bind the pages into a book she can put on the shelf and read aloud. */
    exportToBook: z.boolean().default(true),
    promptStyle: z.enum(['guided', 'free']).default('guided'),
    /** Who she reads the finished book to. */
    readAloudTo: LabelSchema.optional(),
    coverPrompt: SentenceSchema.optional(),
  })
  .superRefine((config, ctx) => {
    const lists = [
      ['characters', config.characters],
      ['settings', config.settings],
      ['problems', config.problems],
      ['resolutions', config.resolutions],
    ] as const
    for (const [name, list] of lists) {
      if (!isUnique(list.map((choice) => choice.id))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [name], message: `${name} ids must be unique` })
      }
    }
    if (config.textEntry.enabled && config.textEntry.mode === 'none') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['textEntry', 'mode'],
        message: 'text entry is on, so say how she writes: dictation or her own spelling',
      })
    }
    if (!config.textEntry.enabled && !config.drawingEnabled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['drawingEnabled'],
        message: 'with no drawing and no writing there is no page to make',
      })
    }
  })

export type StoryBuilderConfig = z.infer<typeof StoryBuilderConfigSchema>

export const StoryBuilderSpecSchema = specFor('story_builder', StoryBuilderConfigSchema)
